import {NullableStation, Station} from "../../stations/station.service.interface";
import {NullableDate, NullableNumber, NullableString, NullableUndefinedBoolean} from "../../../types/nullable-types";
import {FormModel} from "../../../models/forms/form.model";
import {FormFields, IFormField} from "../../../models/forms/form-field.interface";
import {
    IPassengerTypeSelectorsList,
    ITotalPaxOverrideProvider
} from "../../passenger-types/passengers-types.service.interface";
import {computed, IReactionDisposer, makeObservable, observable, reaction, runInAction} from "mobx";
import {FlightScheduleModel} from "../flights-schedule/flight-schedule.model";
import {VoidFlightsScheduleModel} from "../flights-schedule/void-flights-schedule.model";
import {IServiceFactory} from "../../service-factory.interface";
import {
    IAvailabilityOneWaySearchResult,
    IFlightSearchControllerCloneOptions,
    IFlightSearchControllerViewModel,
    IFlightSearchFields,
} from "./flight-search-controller-view-model.interface";
import {
    IDotRezAvailabilitySimpleRequest
} from "../../dot-rez-api/data-contracts/requests/booking/search-simple.request";
import {IFlightsScheduleViewModel} from "../flights-schedule/flights-schedule-view-model.interface";
import {ValidationResultEnum} from "../../../types/validation-result.enum";
import {
    IDotRezSimpleAvailability
} from "../../dot-rez-api/data-contracts/booking/search-simple/search-simple.data-contracts";
import {Check} from "../../../types/type-checking";
import {IFlightScheduleFilter} from "../flights-schedule/flight-schedule-filter.interface";
import {IDotRezBookingSession} from "../../dot-rez-api/session/booking-session/dot-rez-booking.session.interface";
import {FlightsDatesSelectorModel} from "../dates-selection/flights-dates-selector.model";
import {PsoUserAgreementEnum, PsoUserOptionsEnum} from "./pso.enums";
import {IRoundTripLowFareReader} from "../../low-fare/low-fare-readers/low-fare-reader.interface";
import {
    BookingSessionClassesOfService
} from "../../dot-rez-api/session/booking-session/booking-session-classes-of-service";
import {UserSessionStorageKeys} from "../../storage/user-session-storage/user-session-storage-keys";
import {Debounce} from "../../../utils/debounce";
import {TimeSpan} from "../../../types/time-span";
import {IPsoMarket} from "../../stations/pso-market.interface";

interface IFlightSearchControllerModelOptions {
    bookingSession?: IDotRezBookingSession;
    getLowFareReader?: () => IRoundTripLowFareReader;
    psoMarket?: IPsoMarket | null;
    initialFieldsValues?: Partial<IFlightSearchFields>;
    previousUserAgent?: NullableString;
    bookingDate?: NullableDate;
    initialPassengersCount?: Record<string, number>;
}

export class FlightSearchControllerModel extends FormModel<IFlightSearchFields> implements IFlightSearchControllerViewModel, ITotalPaxOverrideProvider {
    constructor(services: IServiceFactory,
                private readonly _storageKey: UserSessionStorageKeys,
                private readonly options?: IFlightSearchControllerModelOptions) {
        super(services);

        this.departureSearchResult = this._createEmptySearchResult();
        this.returnSearchResult = this._createEmptySearchResult();
        this._previousUsedAgent = options?.previousUserAgent ?? null;
        if(options?.bookingSession) {
            this._flightSearchSessionPromise = Promise.resolve(options.bookingSession);
        }

        this._psoMarket = options?.psoMarket ?? null;


        this.datesSelector = new FlightsDatesSelectorModel(this, this.services, options?.getLowFareReader ?? (() => this.services.booking.getLowFareReader()));


        this._updatePsoMarket();

        this._reactions.push(reaction(() => this.services.user.isAuthorized,
            async () => {
                this._resetFlightSearchSession();
                await this._updatePsoMarket();
            }));

        makeObservable<this, '_departureFlightSchedule' | '_returnFlightSchedule' | '_psoMarket'>(this, {
            _departureFlightSchedule: observable.ref,
            _returnFlightSchedule: observable.ref,
            departureFlightSchedule: computed,
            returnFlightSchedule: computed,
            departureSearchResult: observable.ref,
            returnSearchResult: observable.ref,
            _psoMarket: observable.ref
        });
    }

    static async createAsync(services: IServiceFactory,
                       storageKey: UserSessionStorageKeys,
                       options?: IFlightSearchControllerModelOptions): Promise<FlightSearchControllerModel> {
        const searchController = new FlightSearchControllerModel(services, storageKey, options);
        await searchController._updatePsoMarketPromise;
        return searchController;
    }

    departureSearchResult: IAvailabilityOneWaySearchResult;
    returnSearchResult: IAvailabilityOneWaySearchResult;
    private get _useBookingSessionInsteadOfFlightSearchSession(): boolean {
        //if true it means we are on a booking flow session so we must have the count down timer dialog
        //if false it means we are in the home page search and no need for count down timer dialog
        return Boolean(this.options?.bookingSession);
    }

    private _createEmptySearchResult(): IAvailabilityOneWaySearchResult {
        return {

            faresAvailable: {},
            trips: []
        }
    }

    readonly datesSelector: FlightsDatesSelectorModel;

    protected _createFields(): FormFields<IFlightSearchFields> {
        const persistedFieldsValues = this.services.userSessionStorage.getJson<IPersistedSearchParams>(this._storageKey);
        return {
            isOneWayDepartureTrip: this._createField<boolean>({
                fieldName: () => 'Is one way departure trip',
                defaultValue: this.options?.initialFieldsValues?.isOneWayDepartureTrip ?? persistedFieldsValues?.isOneWayDepartureTrip ?? false,
                isRequired: false,
            }),
            isOneWayReturnTrip: this._createField<boolean>({
                fieldName: () => 'Is one way return trip',
                defaultValue: this.options?.initialFieldsValues?.isOneWayReturnTrip ?? persistedFieldsValues?.isOneWayReturnTrip ?? false,
                isRequired: false,
            }),
            departureOrigin: this._createField<Station>({
                fieldName: () => 'Departure origin',
                isRequired: () => true,
                defaultValue: this.options?.initialFieldsValues?.departureOrigin ?? this.services.stations.tryGetStation(persistedFieldsValues?.departureOrigin)
            }),
            departureDestination: this._createField<Station>({
                fieldName: () => 'Departure destination',
                isRequired: () => true,
                defaultValue: this.options?.initialFieldsValues?.departureDestination ?? this.services.stations.tryGetStation(persistedFieldsValues?.departureDestination)
            }),
            returnOrigin: this._createField<Station>({
                fieldName: () => 'Return origin',
                isRequired: false,
                defaultValue: this.options?.initialFieldsValues?.returnOrigin ?? this.services.stations.tryGetStation(persistedFieldsValues?.returnOrigin)
            }),
            returnDestination: this._createField<Station>({
                fieldName: () => 'Return destination',
                isRequired: false,
                defaultValue: this.options?.initialFieldsValues?.returnDestination ?? this.services.stations.tryGetStation(persistedFieldsValues?.returnDestination)
            }),
            psoUserOption: this._createField<PsoUserOptionsEnum>({
               fieldName: () => "",
               isRequired: false,
               defaultValue: this.options?.initialFieldsValues?.psoUserOption ?? persistedFieldsValues?.psoUserOption ?? PsoUserOptionsEnum.None,
               isHidden: () => true
            }),
            psoUserAgreement: this._createField<PsoUserAgreementEnum>({
                fieldName: () => "",
                isRequired: false,
                defaultValue: this.options?.initialFieldsValues?.psoUserAgreement ?? persistedFieldsValues?.psoUserAgreement ?? PsoUserAgreementEnum.None
            }),
            agent: this._createField<string>({
                fieldName: () => "Agent",
                isRequired: false,
                defaultValue: this.options?.initialFieldsValues?.agent ?? persistedFieldsValues?.agent ?? null,
                isHidden: () => true
            }),
            departureDate: this._createField<Date>({
                fieldName: () => 'Departure date',
                isRequired: () => !this.isOneWayReturnTrip,
                defaultValue: this.options?.initialFieldsValues?.departureDate ?? this.services.time.tryConvertToDate(persistedFieldsValues?.departureDate)
            }),
            returnDate: this._createField<Date>({
                fieldName: () => 'Return date',
                isRequired: false, //() => !this.isOneWay,
                defaultValue: this.options?.initialFieldsValues?.returnDate ?? this.services.time.tryConvertToDate(persistedFieldsValues?.returnDate)
            }),
            departureMinPrice: this._createField<NullableNumber>({
                fieldName: () => 'Departure min price',
                isRequired: false,
                defaultValue: this.options?.initialFieldsValues?.departureMinPrice ?? (persistedFieldsValues?.departureMinPrice || null),
            }),
            returnMinPrice: this._createField<NullableNumber>({
                fieldName: () => 'Return min price',
                isRequired: false,
                defaultValue: this.options?.initialFieldsValues?.returnMinPrice ?? (persistedFieldsValues?.returnMinPrice || null),
            }),
            withBlueBenefits: this._createField<boolean>({
                fieldName: () => 'Use Blue Benefits',
                defaultValue: this.options?.initialFieldsValues?.withBlueBenefits ?? persistedFieldsValues?.withBlueBenefits ?? this.services.blueBenefits.isEnabled,
                isRequired: false

            }),
            passengers: this._createField<IPassengerTypeSelectorsList>({
                fieldName: () => 'Passengers',
                defaultValue: this.options?.initialFieldsValues?.passengers ?? this.services.passengerTypes.createReadWritePassengersTypesSelectorsList(() => this.withBlueBenefits, () => this, this.options?.initialPassengersCount ?? persistedFieldsValues?.passengers ?? {}),
                validate: () => {
                    return this._validatePassengersTypes();
                }
            }),
            allowBogo: this._createField<boolean>({
                fieldName: () => 'Allow Bogo',
                defaultValue: this.options?.initialFieldsValues?.allowBogo ?? persistedFieldsValues?.allowBogo ?? false,
                isRequired: false,
            }),
            departureFlightScheduleFilter: this._createField<IFlightScheduleFilter>({
                fieldName: () => 'Departure flight schedule filter',
                isRequired: false,
                defaultValue: this.options?.initialFieldsValues?.departureFlightScheduleFilter ?? null
            }),
            returnFlightScheduleFilter: this._createField<IFlightScheduleFilter>({
                fieldName: () => 'Return flight schedule filter',
                isRequired: false,
                defaultValue: this.options?.initialFieldsValues?.returnFlightScheduleFilter ?? null
            })
        };
    }

    resetFields() {
        runInAction(() => {
            super.clearAllFields();
            this.isOneWayDepartureTrip = false;
            this.isOneWayReturnTrip = false;
            this.withBlueBenefits = false;
            this.allowBogo = false;
            this.fields.passengers.setValue(this.services.passengerTypes.createReadWritePassengersTypesSelectorsList(() => this.withBlueBenefits, () => this, {}));
        });
    }

    get availableDepartureStations(): Station[] {
        return this.services.stations.getAllStations();
    }

    get availableReturnStations(): Station[] {
        if(this.isOneWayReturnTrip) {
            if(this.returnOrigin) {
                return this.returnOrigin.markets;
            } else {
                return [];
            }
        }

        if(this.departureOrigin) {
            return this.departureOrigin.markets;
        }
        return [];


    }

    get recentlySearchedOrigins(): Station[] {
        return this.services.flightSearch.getRecentlySearchedOrigins();
    }

    get recentlySearchedDestinations(): Station[] {
        const stations = this.services.flightSearch.getRecentlySearchedDestinations();
        if(this.isOneWayReturnTrip) {
            return stations.filter(s => s.stationCode !== this.returnOrigin?.stationCode)
        } else {
            return stations.filter(s => s.stationCode !== this.departureOrigin?.stationCode)
        }
    }

    get isOneWay(): boolean {
        return this.isOneWayDepartureTrip || this.isOneWayReturnTrip;
    }

    get isOneWayDepartureTrip(): boolean {
        return this.fields.isOneWayDepartureTrip.value || false;
    }

    set isOneWayDepartureTrip(value: boolean) {
        if(value) {
            this.fields.isOneWayReturnTrip.setValue(false);
        }
        this.fields.isOneWayDepartureTrip.setValue(value);
    }

    get isOneWayReturnTrip(): boolean {
        return this.fields.isOneWayReturnTrip.value || false;
    }

    set isOneWayReturnTrip(value: boolean) {
        if(value) {
            this.fields.isOneWayDepartureTrip.setValue(false);
        }
        this.fields.isOneWayReturnTrip.setValue(value);
    }

    get withBlueBenefits(): boolean {
        return Boolean(this.fields.withBlueBenefits.value);
    }

    set withBlueBenefits(value: boolean) {
        this.fields.withBlueBenefits.setValue(value);
    }

    get departureOrigin(): NullableStation {
        /*
        if(this.isOneWayReturnTrip) {
            return null;
        }

         */
        return this.fields.departureOrigin.value;
    }

    set departureOrigin(value: NullableStation) {
        this.fields.departureOrigin.setValue(value);
    }

    get departureDestination(): NullableStation {
        /*
        if(this.isOneWayReturnTrip) {
            return null;
        }
         */

        return this.fields.departureDestination.value;
    }
    set departureDestination(value: NullableStation) {
        this.fields.departureDestination.setValue(value);
    }

    get returnOrigin(): NullableStation {
        /*if(this.isOneWayDepartureTrip) {
            return null;
        }*/
        return this.fields.returnOrigin.value || this.departureDestination;
    }

    set returnOrigin(value: NullableStation) {
        this.fields.returnOrigin.setValue(value);
    }

    get returnDestination(): NullableStation {
        /*
        if(this.isOneWayDepartureTrip) {
            return null;
        }
*/
        return this.fields.returnDestination.value || this.departureOrigin;
    }

    set returnDestination(value: NullableStation) {
        this.fields.returnDestination.setValue(value);
    }

    get psoUserOption(): PsoUserOptionsEnum {
        return this.fields.psoUserOption.value ?? PsoUserOptionsEnum.None;
    }

    set psoUserOption(value: PsoUserOptionsEnum) {
        this.fields.psoUserOption.setValue(value);
    }

    get psoUserAgreement(): PsoUserAgreementEnum {
        return this.fields.psoUserAgreement.value ?? PsoUserAgreementEnum.None;
    }

    set psoUserAgreement(value: PsoUserAgreementEnum) {
        this.fields.psoUserAgreement.setValue(value);
    }

    get departureDate(): NullableDate {
        if(this.isOneWayReturnTrip) {
            return null;
        }
        return this.fields.departureDate.value;
    }

    set departureDate(value: NullableDate) {
        this.fields.departureDate.setValue(value);
    }

    get departureDateError(): NullableString {
        return this.fields.departureDate.error;
    }

    get returnDate(): NullableDate {
        if(this.isOneWayDepartureTrip) {
            return null;
        }
        return this.fields.returnDate.value;
    }

    set returnDate(value: NullableDate) {
        this.fields.returnDate.setValue(value);
    }

    get returnDateError(): NullableString {
        return this.fields.returnDate.error;
    }

    get passengers(): IPassengerTypeSelectorsList {
        return this.fields.passengers.value!
    }

    set passengers(value: IPassengerTypeSelectorsList) {
        this.fields.passengers.setValue(value);
    }

    get departureMinPrice(): NullableNumber {
        return this.fields.departureMinPrice.value;
    }

    set departureMinPrice(value: NullableNumber) {
        this.fields.departureMinPrice.setValue(value);
    }

    get returnMinPrice(): NullableNumber {
        return this.fields.returnMinPrice.value;
    }

    set returnMinPrice(value: NullableNumber) {
        this.fields.returnMinPrice.setValue(value);
    }

    get allowBogo(): boolean {
        return Boolean(this.fields.allowBogo.value);
    }

    set allowBogo(value: boolean) {
        this.fields.allowBogo.setValue(value);
    }

    get departureFlightScheduleFilter(): IFlightScheduleFilter | null {
        return this.fields.departureFlightScheduleFilter.value;
    }

    set departureFlightScheduleFilter(value: IFlightScheduleFilter | null) {
        this.fields.departureFlightScheduleFilter.setValue(value);
    }

    get returnFlightScheduleFilter(): IFlightScheduleFilter | null {
        return this.fields.returnFlightScheduleFilter.value;
    }

    set returnFlightScheduleFilter(value: IFlightScheduleFilter | null) {
        this.fields.returnFlightScheduleFilter.setValue(value);
    }

    hasErrors(): boolean {
        if(super.hasErrors()) {
            return true;
        }

        return (!this.fields.departureOrigin.value
                && !this.fields.departureDestination.value
                && !this.fields.returnOrigin.value
                && !this.fields.returnDestination.value)
            || (
                !this.fields.departureDate.value
                && !this.fields.returnDate.value
            );

    }


    validateFlightDatesSelection(): ValidationResultEnum {

        if((this.isOneWayDepartureTrip && !this.departureDate)
            || (this.isOneWayReturnTrip && !this.returnDate)) {
            this.services.alert.showError(this.services.language.translate('Please select the flight date'));
            return ValidationResultEnum.Failure;
        }

        if(!this.isOneWay && !this.departureDate) {
            this.services.alert.showError(this.services.language.translate('Please select departure date'));
            return ValidationResultEnum.Failure;
        }

        if(!this.isOneWay && !this.returnDate) {
            this.services.alert.showError(this.services.language.translate('Please select return date'));
            return ValidationResultEnum.Failure;
        }

        return ValidationResultEnum.Success;
    }
    /*
        private async _setWithBlueBenefitsDefault(): Promise<void> {
            if(Check.isNullOrUndefined(this.fields.withBlueBenefits.value)) {
                if(this.services.user.isAuthorized) {
                    const userProfile = this.services.user.profile;
                    await userProfile.waitForProfileInitialization();
                    this.fields.withBlueBenefits.setValue(this.services.blueBenefits.isEnabled);
                } else {
                    this.fields.withBlueBenefits.setValue(this.services.blueBenefits.isEnabled);
                }
            }
        }
    */


    clone(cloneOptions: IFlightSearchControllerCloneOptions): FlightSearchControllerModel {
        const theClone: FlightSearchControllerModel = new FlightSearchControllerModel(this.services, cloneOptions.storageKey, {
            bookingSession: cloneOptions.bookingSession,
            psoMarket: this.psoMarket,
            previousUserAgent: this._previousUsedAgent,
            initialFieldsValues: {
                psoUserOption: this.fields.psoUserOption.value ?? undefined,
                psoUserAgreement: this.fields.psoUserAgreement.value ?? undefined,
                isOneWayDepartureTrip: this.fields.isOneWayDepartureTrip.value ?? undefined,
                isOneWayReturnTrip: this.fields.isOneWayReturnTrip.value ?? undefined,
                withBlueBenefits: this.fields.withBlueBenefits.value ?? undefined,
                departureOrigin: this.fields.departureOrigin.value ?? undefined,
                departureDestination: this.fields.departureDestination.value ?? undefined,
                returnOrigin: this.fields.returnOrigin.value ?? undefined,
                returnDestination: this.fields.returnDestination.value ?? undefined,
                departureDate: this.fields.departureDate.value ?? undefined,
                returnDate: this.fields.returnDate.value ?? undefined,
                passengers: this.services.passengerTypes.createReadWritePassengersTypesSelectorsList(() => theClone.withBlueBenefits, () => theClone, this._serializePassengers()),
                allowBogo: this.fields.allowBogo.value ?? undefined,
                departureMinPrice: this.fields.departureMinPrice.value ?? undefined,
                returnMinPrice: this.fields.returnMinPrice.value ?? undefined,
                agent: this.fields.agent.value ?? undefined,
                departureFlightScheduleFilter: this.fields.departureFlightScheduleFilter.value ?? undefined,
                returnFlightScheduleFilter: this.fields.returnFlightScheduleFilter.value ?? undefined
            }
        });

        runInAction(() => {
            theClone.departureSearchResult = this.departureSearchResult;
            theClone.returnSearchResult = this.returnSearchResult;
            theClone._departureFlightSchedule = this._departureFlightSchedule;
            theClone._returnFlightSchedule = this._returnFlightSchedule;
            this._resetFlightSearchSession();
        });


        return theClone;
    }

    private _reactions: IReactionDisposer[] = [];


    private _previousDogsCount = 0;
    protected _onFieldsCreated(fields: FormFields<IFlightSearchFields>) {
        super._onFieldsCreated(fields);

        this._persistFieldsValues();


        fields.withBlueBenefits.onChange((value: boolean | null) => {
            if(value) {
                const passengers = this.fields.passengers.value;
                if(passengers) {
                    passengers.limit(this.services.user.profile.blueBenefitsSubscription.maxNumberOfPassengers)
                }
            }
        });

        /*
        fields.isOneWayDepartureTrip.onChange(() => {
            this._resetFlightsSchedule();
        });

        fields.isOneWayReturnTrip.onChange(() => {
            this._resetFlightsSchedule();
        });
         */

        fields.departureOrigin.onChange(() => {
            this.fields.departureDestination.setValue(null);
            this._resetFlightsSchedule();
            this._resetUsePsoMarketSelection();
        });

        fields.departureDestination.onChange(() => {
            this._resetFlightsSchedule();
            this._resetUsePsoMarketSelection();
            this.datesSelector.updateAvailableMonths();
        });

        fields.returnOrigin.onChange(() => {
            this.fields.returnDestination.setValue(null);
            this._resetFlightsSchedule();
            this._resetUsePsoMarketSelection();
        });

        fields.returnDestination.onChange(() => {
            this._resetFlightsSchedule();
            this._resetUsePsoMarketSelection();
            this.datesSelector.updateAvailableMonths();
        });

        fields.departureDate.onChange( () => {
            this._switchPsoMarketPromise = this._switchPsoMarket();

        })

        fields.departureFlightScheduleFilter.onChange(() => {
            this._resetFlightsSchedule();
        });

        fields.returnFlightScheduleFilter.onChange(() => {
            this._resetFlightsSchedule();
        });

        fields.psoUserOption.onChange(() => {
            this.fields.psoUserAgreement.setValue(PsoUserAgreementEnum.None);
            if(!this.services.user.isAuthorized) {
                // Only when the user is anonymous we reset the session because for authorized user we always use the user session.
                // For anonymous user on PSO we need to switch agents based on user PSO selection.
                this._updateFlightSearchSessionAgent();
            }

        });

        fields.psoUserAgreement.onChange(() => {
            if(!this.services.user.isAuthorized) {
                // Only when the user is anonymous we reset the session because for authorized user we always use the user session.
                // For anonymous user on PSO we need to switch agents based on user PSO selection.
                this._updateFlightSearchSessionAgent();
            }
        });

        fields.agent.onChange(() => {
            if(!this.services.user.isAuthorized) {
                // Only when the user is anonymous we reset the session because for authorized user we always use the user session.
                // For anonymous user on PSO we need to switch agents based on user PSO selection.
                this._updateFlightSearchSessionAgent();
            }
        })

        this._previousDogsCount = fields.passengers.value?.countDogs() ?? 0;

        this._reactions.push(reaction(() =>  fields.passengers.value?.getAll().map(p => p.count),
                                        () => {
                                            this._persistFieldsValues();
                                            this.fields.passengers.refreshValidationError();
                                            const dogsCount = fields.passengers.value?.countDogs() ?? 0;
                                            if(this._previousDogsCount !== dogsCount) {
                                                this._resetFlightSearchSession();
                                                this._previousDogsCount = dogsCount;
                                            }
                                        }));


    }

    private _switchPsoMarketPromise: Promise<void> = Promise.resolve();
    private async _switchPsoMarket(): Promise<void> {
        if(this.services.navigator.isOnBookingRoute && !this.services.navigator.routes.booking.hasActiveChildRoute) {
            //basically we are not doing this on MMB and check-in
            return;
        }
        /*const possibleMarkets = this.services.stations.getPossiblePsoMarkets(this.departureOrigin, this.departureDestination);
        if(possibleMarkets.length <= 1) {
            return;
        }
         */
        await this._updatePsoMarket();
        await this._updateFlightSearchSessionAgent();
    }

    private _validatePassengersTypes(): NullableString {
        const field = this.fields.passengers;
        if(!field.value) {
            return null;
        }

        if(!this.departureOrigin || !this.departureDestination) {
            return null;
        }


        if(field.value.countAllWithoutInfants() > field.value.maximumNumberOfPassengersAllowedOnBooking) {
            return field.value.passengersMaximumLimitReachedErrorMessage;
        }
        return null;
    }

    private _updatePsoMarketPromise: Promise<void> = Promise.resolve();
    private _updatePsoMarket(): Promise<void> {
        this._updatePsoMarketPromise = this._getPsoMarket().then(psoMarket => {
            this.psoMarket = psoMarket;
            if(!psoMarket) {
                this._clearPsoMarketOptionsSelection();
            }
        }).catch(err => {
            this.services.logger.error('Failed to update PSO market', err);
            this._updatePsoMarketPromise = Promise.resolve();
            this.psoMarket = null;
        });
        return this._updatePsoMarketPromise;
    }

    async getPsoMarketAsync(): Promise<IPsoMarket | null> {
        await this._updatePsoMarket();
        return this.psoMarket;
    }

    protected _onFieldChanged(field: IFormField) {
        super._onFieldChanged(field);
        this._persistFieldsValues();
        this.fields.passengers.refreshValidationError();
    }

    private _resetFlightsSchedule(): void {
        runInAction(() => {
            this.fields.departureDate.setValue(null);
            this.fields.returnDate.setValue(null);
            this._departureFlightSchedule = null;
            this._returnFlightSchedule = null;

        })
    }

    private _clearPsoMarketOptionsSelection() {
        this.fields.psoUserOption.setValue(PsoUserOptionsEnum.None);
        this.fields.psoUserAgreement.setValue(PsoUserAgreementEnum.None);
    }
    private _resetUsePsoMarketSelection(): void {
        this._clearPsoMarketOptionsSelection();
        this._updatePsoMarket();
    }


    private _departureFlightSchedule: IFlightsScheduleViewModel | null = null;
    get departureFlightSchedule(): IFlightsScheduleViewModel {
        return this._getFlightSchedule(this.departureOrigin,
                                       this.departureDestination,
                                        () => null,
                                        () => this._departureFlightSchedule,
                                        schedule => this._departureFlightSchedule = schedule,
                                        this.departureFlightScheduleFilter);
    }

    private _getMinAllowedReturnDate(): NullableDate {
        if(this.isOneWay) {
            return null;
        }

        return this.departureDate;

    }
    private _returnFlightSchedule: IFlightsScheduleViewModel | null = null;
    get returnFlightSchedule(): IFlightsScheduleViewModel {
        return this._getFlightSchedule(
                this.returnOrigin,
                this.returnDestination,
                () =>  this._getMinAllowedReturnDate(),
                () => this._returnFlightSchedule,
                schedule => this._returnFlightSchedule = schedule,
                this.returnFlightScheduleFilter);
    }

    private _getFlightSchedule(origin: NullableStation,
                               destination: NullableStation,
                               getMinAllowedDate: () => NullableDate,
                               getCurrentSchedule: () => IFlightsScheduleViewModel | null,
                               setCurrentSchedule: (schedule: IFlightsScheduleViewModel) => void,
                               scheduleFilter: IFlightScheduleFilter | null): IFlightsScheduleViewModel{

        const currentSchedule = getCurrentSchedule();
        if(currentSchedule) {
            return currentSchedule;
        }

        if(!origin || !destination) {
            return new VoidFlightsScheduleModel();
        }

        if(origin.stationCode === destination.stationCode) {
            return new VoidFlightsScheduleModel();
        }

        const newSchedule = new FlightScheduleModel(this.services, origin, destination, getMinAllowedDate, scheduleFilter);

        runInAction(() => {
            setCurrentSchedule(newSchedule);
        });

        return newSchedule;
    }

    private _persistFieldsValuesDebounce = new Debounce(TimeSpan.fromMilliseconds(100));

    private  _persistFieldsValues(): void {

        this._persistFieldsValuesDebounce.execute(() => {
            const persistedFieldValues: IPersistedSearchParams = {
                isOneWayDepartureTrip: this.fields.isOneWayDepartureTrip.value,
                isOneWayReturnTrip: this.fields.isOneWayReturnTrip.value,
                withBlueBenefits: this.fields.withBlueBenefits.value,
                departureOrigin: this.fields.departureOrigin.value?.stationCode || null,
                departureDestination: this.fields.departureDestination.value?.stationCode || null,
                returnOrigin: this.fields.returnOrigin.value?.stationCode || null,
                returnDestination: this.fields.returnDestination.value?.stationCode || null,
                psoUserOption: this.fields.psoUserOption.value,
                psoUserAgreement: this.fields.psoUserAgreement.value,
                agent: this.fields.agent.value,
                departureDate: this.services.time.formatYYYY_MM_DD(this.fields.departureDate.value),
                returnDate: this.services.time.formatYYYY_MM_DD(this.fields.returnDate.value),
                passengers: this._serializePassengers(),
                departureMinPrice: this.fields.departureMinPrice.value,
                returnMinPrice: this.fields.returnMinPrice.value,
                allowBogo: this.allowBogo
            }
            this.services.userSessionStorage.setJson(this._storageKey, persistedFieldValues);
        });
    }

    private _serializePassengers(): Record<string, number> {
        if(this.fields.passengers.value) {
            return this.fields.passengers.value.getAll().toDictionaryOfType(item => item.getCurrentCode(), item => item.count);
        } else {
            return {}
        }
    }

    dispose(): void {
        this.services.userSessionStorage.removeItem(this._storageKey);
        this._reactions.forEach(disposer => disposer());
        this._reactions = [];
        this.datesSelector.dispose();
    }
/*
    private _returnAndDestinationHaveSameClassOfServices(): boolean {
        const departureClassOfServices = this.departureClassOfServices.sort((c1, c2) => c1.localeCompare(c2))
        const returnClassOfServices = this.returnClassOfServices.sort((c1, c2) => c1.localeCompare(c2))

        if(departureClassOfServices.length !== returnClassOfServices.length) {
            return false;
        }

        for(let i = 0; i < departureClassOfServices.length; i++) {
            if(departureClassOfServices[i] !== returnClassOfServices[i]) {
                return false;
            }
        }

        return true;
    }
*/
    private _isRoundTrip(): boolean {
        return !this.isOneWay
                && this.departureOrigin?.stationCode === this.returnDestination?.stationCode
                && this.departureDestination?.stationCode === this.returnOrigin?.stationCode
                //&& this._returnAndDestinationHaveSameClassOfServices()
                && this.departureMinPrice === this.returnMinPrice;
    }

    private _psoMarket: IPsoMarket | null = null;
    get psoMarket(): IPsoMarket | null {
        return this._psoMarket;
    }

    private set psoMarket(value: IPsoMarket | null) {
        runInAction(() => {
            this._psoMarket = value;
        });
    }

    private _getRoutePsoMarket(): IPsoMarket | null {
        return this.services.stations.getPsoMarket(this.departureOrigin, this.departureDestination, {
            bookingDate: this.options?.bookingDate ?? this.services.time.currentDate,
            departureDate: this.departureDate,
        });
    }

    private _getPsoAgentName(): NullableString {
        if(this.services.user.isAuthorized) {
            return null;
        }

        if(this.passengers.countDogs() > 0) {
            return null;
        }

        if(this.psoUserOption === PsoUserOptionsEnum.None) {
            return null;
        }

        if(this.psoUserAgreement === PsoUserAgreementEnum.DontAgree) {
            return null;
        }

        const psoMarket = this.psoMarket;

        if(!psoMarket) {
            return null;
        }

        return psoMarket.getAgentForUserOption(this.psoUserOption);
    }

    private async _getPsoMarket(): Promise<IPsoMarket | null> {
        const psoMarket = this._getRoutePsoMarket();

        if(!psoMarket) {
            return null;
        }

        if(!this.services.user.isAuthorized) {
            return psoMarket;
        }

        const userClassesOfService = await this._getUserClassesOfService();

        if(psoMarket.canBeAppliedForUserClassesOfService(userClassesOfService)) {
            return psoMarket;
        }

        return null;
    }

    private _createDotRezSessionFromToken(dotRezToken: string): IDotRezBookingSession {
        if(this._useBookingSessionInsteadOfFlightSearchSession) {
            return this.services.dotRezApi.createBookingSession(dotRezToken);
        } else {
            return this.services.dotRezApi.createFlightSearchSession(dotRezToken);
        }
    }

    private async _startNewDotRezSession(): Promise<IDotRezBookingSession> {
        if(this._useBookingSessionInsteadOfFlightSearchSession) {
            return await this.services.user.createBookingSession({
                forDog: this.passengers.countDogs() > 0
            });
        } else {
            return await this.services.user.createFlightSearchSession({
                forDog: this.passengers.countDogs() > 0
            });
        }
    }

    setAnonymousAgentName(agentName:  NullableString): void {
        this.fields.agent.setValue(agentName);
    }


    public getAnonymousAgentName(): NullableString {
        if(this.fields.agent.value) {
            return this.fields.agent.value;
        }

        return this._getPsoAgentName();
    }

    private async _createFlightSearchSession(): Promise<IDotRezBookingSession> {
        let agentName = this.getAnonymousAgentName();
        this._previousUsedAgent = agentName;

        if(agentName) {
            const dotRezToken = await this.services.dotRezApi.getDeepLinkAgentToken({
                agentName: agentName
            });

            return this._createDotRezSessionFromToken(dotRezToken);

        } else {
            return await this._startNewDotRezSession();
        }
    }

    private _flightSearchSessionPromise: Promise<IDotRezBookingSession> | null = null;
    getFlightSearchSession(): Promise<IDotRezBookingSession> {

        if(this._flightSearchSessionPromise) {
            this._flightSearchSessionPromise = this._flightSearchSessionPromise.then(async (session) => {
                const isExpired = await session.isExpired();
                if (isExpired) {
                    return await this._createFlightSearchSession();
                } else {
                    return session;
                }
            }).catch(() => {
                return this._createFlightSearchSession();
            });
        } else {
            this._flightSearchSessionPromise = this._createFlightSearchSession();
        }
        return this._flightSearchSessionPromise;
    }


    private _resetFlightSearchSession(): void {
        this._flightSearchSessionPromise = null;
        this._previousUsedAgent = null;
    }

    private _previousUsedAgent: NullableString = null;
    private _updateFlightSearchSessionAgent(): Promise<IDotRezBookingSession> {

        this._flightSearchSessionPromise = this.getFlightSearchSession().then(async session => {

            const currentAgentName = this.getAnonymousAgentName();

            if(currentAgentName === this._previousUsedAgent) {
                return session;
            }

            this._previousUsedAgent = currentAgentName;

            if(currentAgentName) {
                try {
                    await session.impersonateWithAgent({
                        agent: currentAgentName
                    });
                } catch(err) {
                    this.services.logger.error(`Failed to impersonate with agent ${currentAgentName}`, err);
                }

            } else if(!this.services.user.isAuthorized){
                try {
                    await session.impersonateToAnonymous();
                } catch (err) {
                    this.services.logger.error(`Failed to impersonate to anonymous`, err);
                }

            }

            return session;
        });

        return this._flightSearchSessionPromise;

    }

    async applySearch(): Promise<void> {
        await this._switchPsoMarketPromise;
        const fieldsWithErrors = this.activateErrorsValidation();
        if(fieldsWithErrors.length > 0) {
            this.services.logger.error('Invalid booking search params', fieldsWithErrors.map(f => {
                return {fieldName: f.fieldName, error: f.error}
            }));
            return;
        }


        const session = await this.getFlightSearchSession();

        if(this._isRoundTrip()) {

            const response = await this._availabilitySimple(session,
                                                            this.departureOrigin,
                                                            this.departureDestination,
                                                            this.departureDate,
                                                            this.returnDate,
                                                            this.departureMinPrice);

            runInAction(() => {
                this.departureSearchResult = {
                    trips: response.results[0]?.trips || [],
                    faresAvailable: response.faresAvailable
                }
                this.returnSearchResult = {
                    trips: response.results[1]?.trips || [],
                    faresAvailable: response.faresAvailable
                };
            });

        } else {

            const result = await Promise.all([
                this._searchDepartureAvailability(session),
                this._searchReturnAvailability(session)
            ]);

            runInAction(() => {
                this.departureSearchResult = result[0];
                this.returnSearchResult = result[1];
            });
        }
    }

    async applySearchForDepartureOnly(): Promise<void> {
        await this._switchPsoMarketPromise;
        const session = await this.getFlightSearchSession();

        const response = await this._searchDepartureAvailability(session);

        runInAction(() => {
            this.departureSearchResult = response;
        });
    }

    async applySearchForReturnOnly(): Promise<void> {

        await this._switchPsoMarketPromise;
        const session = await this.getFlightSearchSession();

        const result = await this._searchReturnAvailability(session);

        runInAction(() => {
            this.returnSearchResult = result;
        });
    }

    private async _getUserClassesOfService(): Promise<BookingSessionClassesOfService> {
        const session = await this.getFlightSearchSession();
        return await session.getClassesOfService();

    }

    async getClassesOfServiceForSearch(): Promise<string[]> {
        if(!this.services.user.isAuthorized) {
            return [];
        }

        const market = await this._getPsoMarket();
        if(!market) {
            return [];
        }

        if(!market.isUsingClassesOfService) {
            return [];
        }

        const userClassesOfService = await this._getUserClassesOfService();

        return market.getClassesOfServiceForSearch(userClassesOfService, this.psoUserAgreement, this.psoUserOption);
    }


    private async _availabilitySimple(session: IDotRezBookingSession,
                                      origin: NullableStation,
                                      destination: NullableStation,
                                      beginDate: NullableDate,
                                      endDate: NullableDate,
                                      minPrice: NullableNumber): Promise<IDotRezSimpleAvailability> {

        if(!origin || !destination || !beginDate) {
            this.services.logger.error('Origin, destination and beginDate are mandatory for availability search');
            return {
                faresAvailable: {},
                results: [],
                includeTaxesAndFees: false,
                currencyCode: this.services.currency.defaultCurrency
            }
        }


        const request: IDotRezAvailabilitySimpleRequest = {
            origin: origin.stationMacCode,
            destination: destination.stationMacCode,
            beginDate: this.services.time.formatYYYY_MM_DD(beginDate),
            codes: {
                currencyCode: this.services.booking.activeCurrency,
                promotionCode: (!this.allowBogo || this.passengers.countAllWithoutInfants() === 1) ? "" : "BOGO1"
            },
            passengers: {
                types: this.passengers.createPassengerTypesForAvailabilitySearch()
            },
            filters: {
                maxConnections: 15
            }
        }

        if(endDate) {
            request.endDate = this.services.time.formatYYYY_MM_DD(endDate);
        }

        const classesOfService = await this.getClassesOfServiceForSearch();
        if(classesOfService.length > 0) {
            request.filters.classesOfService = classesOfService;
        }

        if(!Check.isNullOrUndefined(minPrice)) {
            request.filters.minPrice = minPrice;
        }

        return await session.availabilitySimple(request);
    }

    private async _availabilitySimpleOneWay(session: IDotRezBookingSession,
                                            origin: NullableStation,
                                            destination: NullableStation,
                                            beginDate: NullableDate,
                                            minPrice: NullableNumber): Promise<IAvailabilityOneWaySearchResult> {
        const response = await this._availabilitySimple(session, origin, destination, beginDate, null, minPrice);

        return {
            faresAvailable: response.faresAvailable,
            trips: response.results[0]?.trips || []
        };
    }



    private async _searchDepartureAvailability(session: IDotRezBookingSession): Promise<IAvailabilityOneWaySearchResult> {
        if(this.isOneWayReturnTrip) {
            return this._createEmptySearchResult();
        }

        return await this._availabilitySimpleOneWay(session,
                                                    this.departureOrigin,
                                                    this.departureDestination,
                                                    this.departureDate,
                                                    this.departureMinPrice);
    }


    private async _searchReturnAvailability(session: IDotRezBookingSession): Promise<IAvailabilityOneWaySearchResult> {
        if(this.isOneWayDepartureTrip) {
            return this._createEmptySearchResult();
        }

        return await this._availabilitySimpleOneWay(session,
                                                    this.returnOrigin,
                                                    this.returnDestination,
                                                    this.returnDate,
                                                    this.returnMinPrice);
    }


    getPsoTotalPaxOverride(): NullableNumber {
        if(this.psoUserOption !== PsoUserOptionsEnum.None && this.psoMarket && this.departureDate) {
            return this.services.user.profile.getPsoTotalPaxOverride({
                psoMarket: this.psoMarket,
                departureDate: this.departureDate
            });
        }

        return null;
    }
}


interface IPersistedSearchParams {
    isOneWayDepartureTrip: NullableUndefinedBoolean;
    isOneWayReturnTrip: NullableUndefinedBoolean;
    withBlueBenefits: NullableUndefinedBoolean;
    departureOrigin: NullableString;
    departureDestination: NullableString;
    returnOrigin: NullableString,
    returnDestination: NullableString;
    psoUserOption: PsoUserOptionsEnum | null;
    psoUserAgreement: PsoUserAgreementEnum | null;
    agent: NullableString;
    departureDate: NullableString;
    returnDate: NullableString;
    passengers: Record<string, number>;
    departureMinPrice: NullableNumber;
    returnMinPrice: NullableNumber;
    allowBogo: boolean;
}
